import PropTypes from "prop-types";
import AnswerReviewList from "./AnswerReviewList";

const StudentAttemptDetailsModal = ({ isOpen, onClose, attempt }) => {
  if (!isOpen || !attempt) {
    return null;
  }

  const formatDuration = (seconds) => {
    const safeSeconds = Math.max(seconds ?? 0, 0);

    const minutes = Math.floor(safeSeconds / 60);

    const remainingSeconds = safeSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(
      remainingSeconds,
    ).padStart(2, "0")}`;
  };

  const isPassed = attempt.result === "pass";

  return (
    <div
      className="
        fixed
        inset-0
        z-50
        flex
        items-center
        justify-center
        bg-black/50
        p-4
      "
      role="dialog"
      aria-modal="true"
    >
      <div
        className="
          bg-white
          rounded-xl
          shadow-xl
          w-full
          max-w-5xl
          max-h-[90vh]
          overflow-y-auto
        "
      >
        {/* Header */}

        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              Student Attempt Details
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              {attempt.student?.name ?? "Student"}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="
              text-gray-500
              hover:text-gray-800
              text-xl
            "
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Student Information */}

        <div className="p-5 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-gray-500">Student ID</p>

              <p className="font-medium text-gray-800">
                {attempt.student?.studentId ?? "-"}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Phone</p>

              <p className="font-medium text-gray-800">
                {attempt.student?.phone ?? "-"}
              </p>
            </div>

            <div>
              <p className="text-xs text-gray-500">Attempt Number</p>

              <p className="font-medium text-gray-800">
                #{attempt.attemptNumber ?? 0}
              </p>
            </div>
          </div>
        </div>

        {/* Performance Summary */}

        <div className="p-5">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
            <div className="rounded-lg bg-blue-50 p-3 text-center">
              <p className="text-xs text-gray-500">Score</p>

              <p
                className={`
                  font-semibold
                  ${
                    (attempt.obtainedMarks ?? 0) < 0
                      ? "text-red-600"
                      : "text-blue-600"
                  }
                `}
              >
                {attempt.obtainedMarks ?? 0}/{attempt.totalMarks ?? 0}
              </p>
            </div>

            <div className="rounded-lg bg-purple-50 p-3 text-center">
              <p className="text-xs text-gray-500">Percentage</p>

              <p className="font-semibold text-purple-600">
                {attempt.percentage ?? 0}%
              </p>
            </div>

            <div className="rounded-lg bg-green-50 p-3 text-center">
              <p className="text-xs text-gray-500">Correct</p>

              <p className="font-semibold text-green-600">
                {attempt.correctAnswers ?? 0}
              </p>
            </div>

            <div className="rounded-lg bg-red-50 p-3 text-center">
              <p className="text-xs text-gray-500">Wrong</p>

              <p className="font-semibold text-red-600">
                {attempt.wrongAnswers ?? 0}
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-3 text-center">
              <p className="text-xs text-gray-500">Skipped</p>

              <p className="font-semibold text-gray-700">
                {attempt.unansweredQuestions ?? 0}
              </p>
            </div>

            <div className="rounded-lg bg-orange-50 p-3 text-center">
              <p className="text-xs text-gray-500">Time</p>

              <p className="font-semibold text-orange-600">
                {formatDuration(attempt.durationSeconds)}
              </p>
            </div>
          </div>
        </div>

        {/* Result Status */}

        <div className="px-5 pb-3">
          <span
            className={`
              inline-block
              px-3
              py-1
              rounded-full
              text-xs
              font-semibold
              ${
                isPassed
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }
            `}
          >
            {isPassed ? "PASS" : "FAIL"}
          </span>
        </div>

        {/* Answer Review */}

        <div className="px-5 pb-5">
          <AnswerReviewList answers={attempt.answers ?? []} />
        </div>
      </div>
    </div>
  );
};

StudentAttemptDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,

  onClose: PropTypes.func.isRequired,

  attempt: PropTypes.shape({
    attemptNumber: PropTypes.number,

    obtainedMarks: PropTypes.number,

    totalMarks: PropTypes.number,

    percentage: PropTypes.number,

    result: PropTypes.string,

    correctAnswers: PropTypes.number,

    wrongAnswers: PropTypes.number,

    unansweredQuestions: PropTypes.number,

    durationSeconds: PropTypes.number,

    answers: PropTypes.array,

    student: PropTypes.shape({
      name: PropTypes.string,

      phone: PropTypes.string,

      studentId: PropTypes.string,
    }),
  }),
};

StudentAttemptDetailsModal.defaultProps = {
  attempt: null,
};

export default StudentAttemptDetailsModal;
